import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { CountyListResponse, CountyListService } from '@ptl/global-components';
import { NormalizedResponse } from '@ptl/xhr';
import { Subscription } from 'rxjs';
import { County } from 'src/app/angular-elements/shared/interfaces/zip-county-response.interface';
import { FFMRedirectService } from '../../../services/ffmredirect.service';
//import * as moment from 'moment';
import moment from 'moment';
import { Prospect } from '../../../interfaces/prospect.interface';
import { ProspectService } from '../../../services/prospect.service';
import { GoToMarketplaceModalComponent } from '../go-to-marketplace-modal/go-to-marketplace-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { LINE_OF_BUSINESS_ENUM } from '@ptl/account-management';
import { validatorLookupRegistry } from '@ptl/forms';

interface NewQuoteOnExchangeFormControls {
  firstName: AbstractControl;
  lastName: AbstractControl;
  dob: AbstractControl;
  email: AbstractControl;
  emailConfirmation: AbstractControl;
  address1: AbstractControl;
  address2: AbstractControl;
  city: AbstractControl;
  county: AbstractControl;
  state: AbstractControl;
  zipCode: AbstractControl;
}
interface NewQuoteOnExchangeFormControlsAsJson {
  firstName: string;
  lastName: string;
  dob: string;
  email: string;
  emailConfirmation: string;
  address1: string;
  address2: string;
  city: string;
  county: County;
  state: string;
  zipCode: string;
}
@Component({
    selector: 'app-new-quote-on-exchange',
    templateUrl: './new-quote-on-exchange.component.html',
    styleUrls: ['./new-quote-on-exchange.component.scss'],
    standalone: false
})
export class NewQuoteOnExchangeComponent implements OnInit, OnDestroy {

  @Input() selectedMarket: LINE_OF_BUSINESS_ENUM;
  @Output() cancel = new EventEmitter<void>();

  newQuoteOnExchangeForm: UntypedFormGroup;
  counties: County[];
  isSingleCounty = true;
  selectCountyText: string;
  stateValues: any;
  saveableCounty: County;
  saveableZipCode: string;
  zipFormEl: ElementRef;
  whenZipCodeChanges: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private countyListService: CountyListService,
    private ffmRedirectService: FFMRedirectService,
    private prospectService: ProspectService,
    private matDialog: MatDialog
  ) {}

  get formControls(): NewQuoteOnExchangeFormControls {
    return this.newQuoteOnExchangeForm.controls as unknown as NewQuoteOnExchangeFormControls;
  }
  get formControlsAsJson(): NewQuoteOnExchangeFormControlsAsJson {
    return this.newQuoteOnExchangeForm.value as unknown as NewQuoteOnExchangeFormControlsAsJson;
  }

  ngOnInit(): void {
    this.newQuoteOnExchangeForm = this.fb.group({
      firstName: this.fb.control('', [Validators.required, Validators.maxLength(50)]),
      lastName: this.fb.control('', [Validators.required, Validators.maxLength(50)]),
      dob: this.fb.control('', [Validators.required, Validators.maxLength(32)]),
      email: this.fb.control('', [Validators.maxLength(100), validatorLookupRegistry.get('email')]),
      emailConfirmation: this.fb.control('', [Validators.maxLength(100), this.confirmEmailAddress()]),
      address1: this.fb.control('', [Validators.required, Validators.maxLength(75), Validators.pattern(/[A-Za-z0-9-.,#/'& ]{5,75}/)]),
      address2: this.fb.control('', [Validators.maxLength(75), Validators.pattern(/[A-Za-z0-9-.,#/'& ]{5,75}/)]),
      city: this.fb.control('', [Validators.required]),
      county: this.fb.control('', [Validators.required]),
      state: this.fb.control('', [Validators.required]),
      zipCode: this.fb.control('', [Validators.required])
    });

    this.countyReset();

    this.whenZipCodeChanges = this.newQuoteOnExchangeForm.get('zipCode').valueChanges.subscribe(value => {
      this.newQuoteOnExchangeForm.controls.county.reset();
      if (value?.length === 5) {
        this.getCountyInfo(value);
        this.saveableZipCode = value;

      } else {
        this.countyReset();
      }
    });
  }

  ngOnDestroy() {
    if (this.whenZipCodeChanges) {
      this.whenZipCodeChanges.unsubscribe();
    }
  }

  async submit() {
    if (this.newQuoteOnExchangeForm.valid) {

      const formedProspect: Prospect = {
        firstName: this.formControlsAsJson.firstName,
        lastName: this.formControlsAsJson.lastName,
        dob: moment(this.formControlsAsJson.dob).toDate().getTime(),
        email: this.formControlsAsJson.email,
        address1: this.formControlsAsJson.address1,
        address2: this.formControlsAsJson.address2,
        city: this.formControlsAsJson.city,
        state: this.formControlsAsJson.state,
        zipCode: this.formControlsAsJson.zipCode,
        shoppingInfo: {
          brand: this.saveableCounty.brand,
          zipCode: this.formControlsAsJson.zipCode,
        },
        county: this.formControlsAsJson.county.county,
        applicationType: 'ONEX'
      };
      /** remove empty values */
      Object.keys(formedProspect).forEach(k => {
        if (!formedProspect[k]) {
          delete formedProspect[k];
        }
      });
      const response = await this.prospectService.createProspect(formedProspect, this.selectedMarket);
      formedProspect.prospectId = response.payload.prospects[0].prospectId;

      this.matDialog.open(GoToMarketplaceModalComponent, {
        width: '50vw',
        height: '80vh',
        disableClose: false,
        data: {
          prospects: [response.payload.prospects[0]],
          selectedProspect: response.payload.prospects[0],
          countyExcludedFromONX: false // too tired to worry about future proofing this if HM comes back, sorry
        }
      });
    }
  }

  private countyReset() {
    this.newQuoteOnExchangeForm.controls.county.setValue('');
    this.newQuoteOnExchangeForm.controls.state.setValue('');
    this.counties = [];
    this.selectCountyText = 'County';
    this.isSingleCounty = true;
  }

  confirmEmailAddress(): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value && control.value.length > 0) {
        if (control.value === this.newQuoteOnExchangeForm.controls['email'].value) { return null; }
        else {
          return { emailAddressesNotSame: true };
        }
      } else {
        return null;
      }
    };
  }


  async getCountyInfo(zipCode: string) {
    try {
      const response: NormalizedResponse<CountyListResponse> = await this.countyListService.getCountyInformation(zipCode);
      this.counties = response.payload.countyList;
      this.newQuoteOnExchangeForm.controls.county.reset();

      if (this.counties.length > 1) {

        this.isSingleCounty = false;
        this.selectCountyText = 'Select County';
        this.focusOnSelect();

      } else if (this.counties.length === 1) {

        this.isSingleCounty = true;
        this.selectCountyText = 'County';
        this.newQuoteOnExchangeForm.controls.county.setValue(this.counties[0]);
        this.newQuoteOnExchangeForm.controls.state.setValue(this.counties[0].state);
        this.stateValues = this.counties[0];
        this.saveableCounty = this.counties[0];
        this.countySelected(this.counties[0]);

      }

    } catch (e) {
      this.newQuoteOnExchangeForm.controls.zipCode.setErrors({ wrongZipForRegion: true });
    }
  }
  focusOnSelect() {
    // need a slight delay for change detection to run so it adds the select input
    setTimeout(() => {
      this.zipFormEl.nativeElement.getElementsByTagName('mat-select')[0].focus();
    }, 100);
  }
  countySelected(county: County) {
    this.saveableCounty = county;
    this.newQuoteOnExchangeForm.controls.state.setValue(county.state);
  }
  onCountyMultipleChange(event: MatSelectChange) {
    const countyData: County = event.value as County;
    this.countySelected(countyData);
    this.selectCountyText = 'County';
  }

  cancelCreation() {
    this.cancel.next();
  }
}
